import { useEffect, useState } from "react";

const STORAGE_KEY = "cookie_consent";
const GA_ID = process.env.REACT_APP_GA_ID;
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

/**
 * Reads current consent from localStorage (via state + storage/consent-updated events).
 * Rendered ONCE at the App root — conditionally injects GA + Meta Pixel <script>
 * tags only after the user granted the matching consent.
 *
 * IMPORTANT: this component MUST NOT create any window.gtag/window.fbq stubs
 * before consent is granted, otherwise events would silently queue and fire
 * as soon as the script loads (which would break CNIL compliance).
 */
export const ConsentScripts = () => {
  const [consent, setConsent] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  });

  // Keep consent state in sync with updates from the banner.
  useEffect(() => {
    const onUpdate = (e) => setConsent(e.detail || null);
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        try {
          setConsent(e.newValue ? JSON.parse(e.newValue) : null);
        } catch (err) {
          setConsent(null);
        }
      }
    };
    window.addEventListener("consent-updated", onUpdate);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("consent-updated", onUpdate);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // Google Analytics 4 — inject on analytics=true only.
  useEffect(() => {
    if (!consent?.analytics || !GA_ID || window.gtag) return;
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    script.setAttribute("data-consent-driven", "ga");
    document.head.appendChild(script);
    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line no-inner-declarations
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;
    window.gtag("js", new Date());
    window.gtag("config", GA_ID, { anonymize_ip: true });
  }, [consent?.analytics]);

  // Meta Pixel — inject on advertising=true only.
  useEffect(() => {
    if (!consent?.advertising || !META_PIXEL_ID || window.fbq) return;
    // eslint-disable-next-line
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){
      n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;
      t.setAttribute('data-consent-driven','meta');
      s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s);}(
      window,document,"script","https://connect.facebook.net/en_US/fbevents.js");
    window.fbq("init", META_PIXEL_ID);
    window.fbq("track", "PageView");
  }, [consent?.advertising]);

  return null;
};

export default ConsentScripts;
